import React, { useEffect, useMemo, useState } from 'react';
import { buildTriviaPreviewHtml } from '../../utils/pluginPreviewHtml';
import PluginRenderingBar from './PluginRenderingBar';
import { getTriviaToday } from '../../services/contentPlugins';
import './ContentPlugins.css';

const RENDERING_MESSAGE = 'Generating your trivia videos… You can close this window — we\'ll keep working in the background.';

const DEFAULT_CATEGORIES = [
  { id: 9, name: 'General Knowledge' },
  { id: 11, name: 'Film' },
  { id: 12, name: 'Music' },
  { id: 14, name: 'Television' },
  { id: 15, name: 'Video Games' },
  { id: 17, name: 'Science & Nature' },
  { id: 21, name: 'Sports' },
  { id: 22, name: 'Geography' },
  { id: 23, name: 'History' },
  { id: 26, name: 'Celebrities' },
  { id: 27, name: 'Animals' },
  { id: 32, name: 'Cartoon & Animations' },
];

function isVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const path = url.split('?')[0].toLowerCase();
  return path.endsWith('.mp4') || path.endsWith('.webm') || path.endsWith('.mov') || path.endsWith('.m4v');
}

function isImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const path = url.split('?')[0].toLowerCase();
  return path.endsWith('.png') || path.endsWith('.jpg') || path.endsWith('.jpeg') || path.endsWith('.webp') || path.endsWith('.gif');
}

function normalizeInitialCategoryIds(initialConfig, allIds) {
  const raw = initialConfig?.categoryIds;
  if (!Array.isArray(raw) || raw.length === 0) return [...allIds];
  const selected = raw.map(Number).filter((id) => allIds.includes(id));
  return selected.length ? selected : [...allIds];
}

export default function TriviaPluginConfig({
  open,
  onClose,
  onSave,
  saving,
  rendering = false,
  contentUrl = null,
  token = null,
  orientation = 'landscape',
  initialConfig = null,
}) {
  const [previewHtml, setPreviewHtml] = useState('');
  const [videoFailed, setVideoFailed] = useState(false);
  const [dailyUrls, setDailyUrls] = useState([]);
  const [clipIndex, setClipIndex] = useState(0);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedIds, setSelectedIds] = useState(() => DEFAULT_CATEGORIES.map((c) => c.id));

  const allIds = useMemo(() => categories.map((c) => c.id), [categories]);

  const playlist = useMemo(() => {
    const fromApi = (dailyUrls || []).filter(isVideoUrl);
    if (fromApi.length > 0) return fromApi;
    return isVideoUrl(contentUrl) ? [contentUrl] : [];
  }, [dailyUrls, contentUrl]);

  const activeUrl = playlist[clipIndex] || null;
  const showVideo = Boolean(activeUrl) && !videoFailed;
  const showImage = !showVideo && isImageUrl(contentUrl);
  const showHtmlPreview = !showVideo && !showImage;

  useEffect(() => {
    if (!open) {
      setVideoFailed(false);
      setDailyUrls([]);
      setClipIndex(0);
      return undefined;
    }
    setVideoFailed(false);
    setClipIndex(0);
    setSelectedIds(normalizeInitialCategoryIds(initialConfig, DEFAULT_CATEGORIES.map((c) => c.id)));
    if (showHtmlPreview) {
      setPreviewHtml(buildTriviaPreviewHtml({}));
    }

    let cancelled = false;
    if (token) {
      getTriviaToday(token, orientation)
        .then((res) => {
          if (cancelled) return;
          const cats = res.data?.categories?.length ? res.data.categories : DEFAULT_CATEGORIES;
          setCategories(cats);
          const catIds = cats.map((c) => c.id);
          setSelectedIds((prev) => {
            const fromInitial = normalizeInitialCategoryIds(initialConfig, catIds);
            if (initialConfig?.categoryIds?.length) return fromInitial;
            const kept = prev.filter((id) => catIds.includes(id));
            return kept.length ? kept : fromInitial;
          });
          const urls = (res.data?.urls || []).filter(isVideoUrl);
          setDailyUrls(urls);
        })
        .catch(() => {
          if (!cancelled) setDailyUrls([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open, contentUrl, showHtmlPreview, token, orientation, initialConfig]);

  useEffect(() => {
    if (clipIndex >= playlist.length) setClipIndex(0);
  }, [playlist.length, clipIndex]);

  if (!open) return null;

  const readOnly = rendering;
  const alreadyLive = Boolean(contentUrl) && !rendering && (showVideo || showImage);
  const canSave = selectedIds.length > 0;

  const handleSave = () => {
    if (readOnly || !canSave) return;
    const categoryIds = selectedIds.length >= allIds.length ? allIds : selectedIds;
    onSave({ categoryIds });
  };

  const toggleCategory = (id) => {
    if (readOnly) return;
    setSelectedIds((prev) => (
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    ));
  };

  const selectAll = () => {
    if (readOnly) return;
    setSelectedIds([...allIds]);
  };

  const handleVideoEnded = () => {
    if (playlist.length <= 1) return;
    setClipIndex((i) => (i + 1) % playlist.length);
  };

  let previewLabel = 'Preview';
  if (showVideo) {
    previewLabel = playlist.length > 1
      ? `Today's trivia (${clipIndex + 1} of ${playlist.length})`
      : "Today's trivia video";
  } else if (showImage) {
    previewLabel = 'Trivia preview (video still rendering)';
  }

  return (
    <div className="contentPluginOverlay" role="presentation">
      <div
        className={`contentPluginConfigModal ${readOnly ? 'contentPluginConfigReadOnly' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="contentPluginConfigHeader">
          <h2>Trivia Plugin</h2>
          <button type="button" className="contentPluginClose" onClick={onClose}>×</button>
        </div>
        {rendering ? (
          <PluginRenderingBar message={RENDERING_MESSAGE} />
        ) : null}
        <div className="contentPluginConfigBody">
          <div className="contentPluginForm">
            <p className="contentPluginHint">
              Turn heads and spark conversation with daily trivia your customers will stop to play along with.
              Fresh questions keep people watching longer — and give your space something fun to talk about.
            </p>
            <p className="contentPluginSectionLabel">Categories</p>
            <div className="contentPluginSportActions">
              <button type="button" onClick={selectAll} disabled={readOnly}>
                Select all
              </button>
            </div>
            <div className="contentPluginChips">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`contentPluginChip ${selectedIds.includes(cat.id) ? 'active' : ''}`}
                  onClick={() => toggleCategory(cat.id)}
                  disabled={readOnly}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            {!canSave ? (
              <p className="contentPluginHint" style={{ marginTop: 12 }}>
                Choose at least one category.
              </p>
            ) : null}
            {alreadyLive && showVideo ? (
              <p className="contentPluginSelected">
                Trivia is live on your screen. Open the preview anytime for a taste of today&apos;s questions.
              </p>
            ) : null}
          </div>
          <div className="contentPluginPreviewPane">
            <p className="contentPluginPreviewLabel">{previewLabel}</p>
            <div className="contentPluginPreviewFrameWrap">
              {showVideo ? (
                <video
                  key={activeUrl}
                  className="contentPluginPreviewFrame contentPluginPreviewVideo"
                  src={activeUrl}
                  controls
                  autoPlay
                  muted
                  loop={playlist.length <= 1}
                  playsInline
                  onEnded={handleVideoEnded}
                  onError={() => setVideoFailed(true)}
                />
              ) : null}
              {showImage ? (
                <img
                  key={contentUrl}
                  className="contentPluginPreviewFrame contentPluginPreviewVideo"
                  src={contentUrl}
                  alt="Trivia preview"
                />
              ) : null}
              {showHtmlPreview ? (
                <iframe
                  title="Trivia preview"
                  className="contentPluginPreviewFrame"
                  srcDoc={previewHtml}
                  sandbox="allow-scripts"
                />
              ) : null}
            </div>
          </div>
        </div>
        <div className="contentPluginConfigFooter">
          <button type="button" className="contentPluginCancel" onClick={onClose}>
            {rendering ? 'Close' : alreadyLive ? 'Done' : 'Cancel'}
          </button>
          {!rendering ? (
            <button
              type="button"
              className="contentPluginSave"
              onClick={handleSave}
              disabled={saving || !canSave}
            >
              {saving
                ? (alreadyLive ? 'Refreshing…' : 'Saving…')
                : (alreadyLive ? 'Save Categories' : 'Add Trivia')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
